import React, { useRef, useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faImage,
  faXmark,
  faPlus,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons";

const BRAND = {
  primary:
    "bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] disabled:bg-gray-300 disabled:text-gray-500",
  ghost:
    "bg-white border border-[#14251F] text-[#14251F] hover:bg-[#14251F] hover:text-[#FFD1A0]",
  hint: "text-[#D48C1E]",
  thumbActive: "ring-2 ring-[#D48C1E]",
  iconMuted: "text-[#14251F]/30",
};

const aspectClass = (aspect) =>
  aspect === "square"
    ? "aspect-square"
    : aspect === "wide"
    ? "aspect-[16/9]"
    : aspect === "tall"
    ? "aspect-[3/4]"
    : "aspect-[4/3]";

// ── Zoom popup ────────────────────────────────────────────────────────────
function ImageZoomModal({ items, index, onClose, onNavigate }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate?.(1);
      if (e.key === "ArrowLeft") onNavigate?.(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNavigate]);

  if (!items?.length) return null;
  const current = items[index];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white/90 hover:text-white w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full"
        aria-label="Close"
      >
        <FontAwesomeIcon icon={faXmark} size="lg" />
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate?.(-1);
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate?.(1);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-3 py-1 rounded-full">
            {index + 1} / {items.length}
          </div>
        </>
      )}

      <img
        src={current.src}
        alt={current.alt || "preview"}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded shadow-2xl"
      />
    </div>
  );
}

// ── Single image field ────────────────────────────────────────────────────
/**
 * Props: label, hint, previewUrl, file, onChange(e), onClear(),
 *        accept, aspect ("square"|"wide"|"4/3"|"tall"),
 *        buttonLabel, required, disabled, id
 */
export default function ImageUploadField({
  label = "Image",
  hint,
  previewUrl,
  file,
  onChange,
  onClear,
  accept = "image/*",
  aspect = "4/3",
  buttonLabel,
  required = false,
  disabled = false,
  id,
}) {
  const inputRef = useRef(null);
  const [localPreview, setLocalPreview] = useState("");
  const [zoom, setZoom] = useState(false);
  const inputId =
    id || `image-upload-${Math.random().toString(36).slice(2, 9)}`;

  useEffect(() => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setLocalPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setLocalPreview("");
  }, [file]);

  const displaySrc = localPreview || previewUrl || "";
  const fileName = file?.name || "";

  const handlePick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    if (inputRef.current) inputRef.current.value = "";
    onClear?.();
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1.5"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div
        className={`flex gap-3 sm:gap-4 items-stretch p-2 sm:p-3 border-2 border-dashed rounded-lg bg-gray-50 transition-colors border-gray-300 hover:border-[#D48C1E] ${
          disabled ? "opacity-60" : ""
        }`}
      >
        {/* Left preview */}
        <div
          className={`relative shrink-0 w-28 sm:w-36 ${aspectClass(
            aspect
          )} bg-gray-100 rounded-md overflow-hidden flex items-center justify-center group`}
        >
          {displaySrc ? (
            <>
              <img
                src={displaySrc}
                alt="preview"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => setZoom(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 text-white/0 group-hover:text-white transition-all"
                title="View full size"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} size="lg" />
              </button>
              {onClear && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute top-1 right-1 bg-white/95 hover:bg-white text-red-500 rounded-full w-6 h-6 flex items-center justify-center shadow"
                  title="Remove"
                >
                  <FontAwesomeIcon icon={faXmark} size="sm" />
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={handlePick}
              disabled={disabled}
              className={`w-full h-full flex flex-col items-center justify-center gap-1 ${BRAND.iconMuted} ${
                disabled ? "cursor-not-allowed" : "hover:text-[#14251F]"
              }`}
            >
              <FontAwesomeIcon icon={faImage} size="2x" />
              <span className="text-[10px]">No image</span>
            </button>
          )}
        </div>

        {/* Right action — stacked vertically, right-aligned */}
        <div className="flex-1 flex flex-col justify-center items-end gap-1.5 min-w-0">
          <button
            type="button"
            onClick={handlePick}
            disabled={disabled}
            className={`inline-flex items-center gap-2 text-sm font-medium px-3.5 py-1.5 rounded-md transition-colors ${BRAND.primary}`}
          >
            <FontAwesomeIcon icon={faCloudArrowUp} />
            {buttonLabel || (displaySrc ? "Change Image" : "Upload Image")}
          </button>
          <span
            className="text-xs text-gray-600 truncate max-w-full text-right"
            title={fileName}
          >
            {fileName || "No file chosen"}
          </span>
          {hint && (
            <p className={`text-xs ${BRAND.hint} leading-snug text-right`}>
              {hint}
            </p>
          )}
        </div>

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          onChange={onChange}
          disabled={disabled}
          className="hidden"
        />
      </div>

      {zoom && (
        <ImageZoomModal
          items={[{ src: displaySrc, alt: fileName }]}
          index={0}
          onClose={() => setZoom(false)}
        />
      )}
    </div>
  );
}

// ── Multi image field ─────────────────────────────────────────────────────
/**
 * Props:
 *  - label, hint, required, disabled, accept, aspect
 *  - existingUrls: array of saved image URLs
 *  - newFiles:     array of File objects newly added
 *  - onAdd(files): user picked new files
 *  - onRemove(idx, isExisting)
 */
export function MultiImageUploadField({
  label = "Images",
  hint,
  existingUrls = [],
  newFiles = [],
  onAdd,
  onRemove,
  accept = "image/*",
  aspect = "4/3",
  required = false,
  disabled = false,
  id,
}) {
  const inputRef = useRef(null);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [filePreviews, setFilePreviews] = useState([]);
  const inputId =
    id || `multi-upload-${Math.random().toString(36).slice(2, 9)}`;

  useEffect(() => {
    const urls = newFiles.map((f) =>
      f instanceof File ? URL.createObjectURL(f) : ""
    );
    setFilePreviews(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [newFiles]);

  const allItems = [
    ...existingUrls.map((url) => ({ src: url, isExisting: true })),
    ...filePreviews.map((src, i) => ({
      src,
      isExisting: false,
      fileName: newFiles[i]?.name,
    })),
  ];

  useEffect(() => {
    if (active >= allItems.length) setActive(Math.max(0, allItems.length - 1));
  }, [allItems.length, active]);

  const handlePick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) onAdd?.(picked);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = useCallback(
    (idx) => {
      const item = allItems[idx];
      if (!item) return;
      if (item.isExisting) {
        onRemove?.(idx, true);
      } else {
        onRemove?.(idx - existingUrls.length, false);
      }
    },
    [allItems, existingUrls.length, onRemove]
  );

  const current = allItems[active];

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1.5"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
          <span className="ml-2 text-xs text-gray-500 font-normal">
            ({allItems.length} {allItems.length === 1 ? "image" : "images"})
          </span>
        </label>
      )}

      <div
        className={`flex gap-3 sm:gap-4 items-stretch p-2 sm:p-3 border-2 border-dashed rounded-lg bg-gray-50 transition-colors border-gray-300 hover:border-[#D48C1E] ${
          disabled ? "opacity-60" : ""
        }`}
      >
        {/* Left preview */}
        <div
          className={`relative shrink-0 w-28 sm:w-36 ${aspectClass(
            aspect
          )} bg-gray-100 rounded-md overflow-hidden flex items-center justify-center group`}
        >
          {current ? (
            <>
              <img
                src={current.src}
                alt={current.fileName || `preview ${active + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => setZoom(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 text-white/0 group-hover:text-white transition-all"
                title="View full size"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} size="lg" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(active);
                }}
                className="absolute top-1 right-1 bg-white/95 hover:bg-white text-red-500 rounded-full w-6 h-6 flex items-center justify-center shadow"
                title="Remove"
              >
                <FontAwesomeIcon icon={faXmark} size="sm" />
              </button>
              {!current.isExisting && (
                <span className="absolute bottom-1 left-1 bg-[#D48C1E] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                  NEW
                </span>
              )}
              {allItems.length > 1 && (
                <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                  {active + 1}/{allItems.length}
                </div>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={handlePick}
              disabled={disabled}
              className={`w-full h-full flex flex-col items-center justify-center gap-1 ${BRAND.iconMuted} ${
                disabled ? "cursor-not-allowed" : "hover:text-[#14251F]"
              }`}
            >
              <FontAwesomeIcon icon={faImage} size="2x" />
              <span className="text-[10px]">No images</span>
            </button>
          )}
        </div>

        {/* Right action area — stacked vertically, right-aligned */}
        <div className="flex-1 flex flex-col justify-center items-end gap-2 min-w-0">
          <button
            type="button"
            onClick={handlePick}
            disabled={disabled}
            className={`inline-flex items-center gap-2 text-sm font-medium px-3.5 py-1.5 rounded-md transition-colors ${BRAND.primary}`}
          >
            <FontAwesomeIcon icon={allItems.length === 0 ? faCloudArrowUp : faPlus} />
            {allItems.length === 0 ? "Upload Images" : "Add More"}
          </button>

          {/* Thumbnail strip — directly below the button, right-aligned */}
          {allItems.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto max-w-full justify-end pb-1">
              {allItems.map((it, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => setActive(idx)}
                  className={`relative shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden border ${
                    idx === active ? BRAND.thumbActive : "border-gray-200"
                  }`}
                  title={it.fileName || `Image ${idx + 1}`}
                >
                  <img
                    src={it.src}
                    alt={`thumb ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {!it.isExisting && (
                    <span className="absolute inset-x-0 bottom-0 bg-[#D48C1E] text-white text-[8px] text-center leading-3">
                      NEW
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {hint && (
            <p className={`text-xs ${BRAND.hint} leading-snug text-right`}>
              {hint}
            </p>
          )}
        </div>

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple
          onChange={handleFile}
          disabled={disabled}
          className="hidden"
        />
      </div>

      {zoom && (
        <ImageZoomModal
          items={allItems.map((it, i) => ({
            src: it.src,
            alt: it.fileName || `Image ${i + 1}`,
          }))}
          index={active}
          onClose={() => setZoom(false)}
          onNavigate={(dir) =>
            setActive((a) => (a + dir + allItems.length) % allItems.length)
          }
        />
      )}
    </div>
  );
}
